import { wallOpeningRect2 } from "building-common/openings/openingQuery";
import { WallLateralDatumAnchorAll } from "building-types/datums/datumsTypes";
import type {
  DormerHandle,
  OpeningHandle,
  RoofOpeningHandle,
  RoomLayoutInstanceHandle,
} from "building-types/handles/docEntityHandleTypes";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { RoomOutlines } from "building-types/rooms/roomOutlineTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import { ShutterShapeType, ShutterType } from "building-types/settings/settingsTypesEnum";
import { ShutterViewNoteTypes } from "building-types/settings/viewNotesSettings";
import type { SimpleSlatShutterSettings } from "building-types/settings/windowShutterSettings";
import { WindowPanelType, WindowShapeTypes } from "building-types/settings/windowTypesEnum";
import { ViewEntityType } from "building-types/views/viewEntityType";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import type { GeneratedWindowPlacement, WindowShutters } from "building-types/windows/windowTypes";
import { MIN_VALID_SHUTTER_HEIGHT, MIN_VALID_SHUTTER_WIDTH } from "common/constants/buildingObjects";
import { ensureDefined } from "common/utils/assertOfType";
import uid from "common/utils/uid";
import { WINDOW_EXTERIOR_FRAME_DEPTH } from "doc/elements/window/constants";
import { isWalledRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { exteriorMaterialOverrideNew } from "doc/utils/materialTools";
import { viewEntityDescriptorNew } from "doc/utils/viewEntityTools";
import { aabb2Height } from "geom/aabb2Types";
import { arc2ByCenterStartAngle } from "geom/arc";
import type { Extrusion } from "geom/brep";
import { faceNew } from "geom/brep";
import { DIST_EPSILON } from "geom/core";
import { extrudeFaceAlongNormal, extrudeFaceAlongPolyLine3 } from "geom/extrude";
import type { Geom } from "geom/geom";
import { line2MidPt } from "geom/line2";
import { offsetPolyLine2, polyLine2ThickenedEdge } from "geom/offset";
import { planeCopyMoved, planeMoveAlongNormal, planeNormal, polyLine2OnPlane } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2AABB2,
  polyLine2Close,
  polyLine2ConcatAll,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeLength,
  polyLine2ExtendEnd,
  polyLine2ExtendStart,
  polyLine2Move,
  polyLine2MoveCopy,
  polyLine2NonUniformScaleAndKeepOrigin,
  polyLine2Reflect,
  polyLine2Reverse,
  polyLine2ReverseCopy,
  polyLine2ScaleXAndKeepOrigin,
  polyLine2SetCW,
  polyLine2Simplify,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { ray2New } from "geom/ray2";
import { rect2Extend, rect2MultiSplitFlex } from "geom/rect2";
import { uvGenerationForWindowShutter } from "../utils/uvGenerationForEntities";
import { entityLateralOffsetFromWallDatum } from "./openings/openingWallLayers";
import { windowShuttersNew } from "./window/windowTools";
import type { WindowShuttersPlacement } from "./window/windowUtils";
import { makeGlassPlane, offsetInnerArchedPanel, splitArcPlRay, ventExtrusion } from "./window/windowUtils";

interface MakeWindowShuttersOptions {
  handle: OpeningHandle | RoofOpeningHandle | DormerHandle | RoomLayoutInstanceHandle | undefined;
  window: GeneratedWindowPlacement;
  shuttersPlacement: WindowShuttersPlacement;
  roomOutlines: RoomOutlines | undefined;
  wallMassSystem: WallMassSystem | undefined;
  issues: BuildingGenerationIssue[];
  minZ: number;
  /** Used for roof and dormer windows, which have no rooms to read the hosting instance from. */
  fallbackInstanceId?: string;
}

export function makeWindowShutters({
  handle,
  window,
  shuttersPlacement,
  roomOutlines,
  wallMassSystem,
  issues,
  minZ,
  fallbackInstanceId,
}: MakeWindowShuttersOptions): WindowShutters | null {
  const opening = window.opening;

  if (!opening) {
    return null;
  }

  if (shuttersPlacement.size < MIN_VALID_SHUTTER_WIDTH) {
    issues.push({
      type: BuildingGenerationIssueType.INVALID_WINDOW_SHUTTER_SIZE,
      handle,
    });

    return null;
  }

  if (shuttersPlacement.height < MIN_VALID_SHUTTER_HEIGHT) {
    issues.push({
      type: BuildingGenerationIssueType.INVALID_WINDOW_SHUTTER_HEIGHT,
      handle,
    });

    return null;
  }

  const windowShutters = windowShuttersNew(window, fallbackInstanceId);
  const { shape, arched, archRadius } = window.descriptor;
  const { settings: shutterSettings } = shuttersPlacement;
  const panelType = window.materials?.panelType;

  let isArched = shape === WindowShapeTypes.RECTANGLE_ARCHED_TOP || (shape === WindowShapeTypes.RECTANGLE && arched);
  let shutterArchRadius: number | undefined = archRadius ?? window.descriptor.width / 2;
  let shutterMaterial: ExteriorMaterial = "shutter0";

  if (
    shutterSettings &&
    shutterSettings.shutterType !== ShutterType.DISABLED &&
    panelType !== WindowPanelType.FAUX_SHUTTER
  ) {
    shutterMaterial = shutterSettings.shutterMaterial ?? shutterMaterial;
    isArched =
      shutterSettings?.shutterShapeOverride === ShutterShapeType.ARCHED ||
      (isArched && !shutterSettings?.shutterShapeOverride);
    shutterArchRadius = shutterSettings?.shutterArchRadiusOverride ?? shutterArchRadius;
  }

  if (!isArched) {
    shutterArchRadius = undefined;
  }

  const shutterViewEntityDescriptor = viewEntityDescriptorNew(
    uid(),
    ViewEntityType.SHUTTER,
    shuttersPlacement?.viewNotes?.[ShutterViewNoteTypes.SHUTTER],
  );

  let exteriorFacePlane = opening.backPlane;
  let exteriorOutline = wallOpeningRect2(opening, "BACK");

  switch (window.type) {
    case "WINDOW": {
      if (window.wallStripDomain && !isWalledRoomEnclosure[ensureDefined(window.frontRoom).attributes.roomEnclosure]) {
        exteriorFacePlane = opening.frontPlane;
        exteriorOutline = wallOpeningRect2(opening, "FRONT");
      }
      break;
    }
    case "WINDOW_V2": {
      if (window.frontRoom && !isWalledRoomEnclosure[window.frontRoom.attributes.roomEnclosure]) {
        exteriorFacePlane = opening.frontPlane;
        exteriorOutline = wallOpeningRect2(opening, "FRONT");
      }
      break;
    }
  }

  let glassPlane = makeGlassPlane(exteriorFacePlane);

  if (shutterSettings && shutterSettings.shutterType !== ShutterType.DISABLED) {
    let resolvedLateralOffset = shutterSettings.lateralOffset ?? 0;
    const edgeIndex = window.edgeId ? window.frontRoom?.edgeIds.indexOf(window.edgeId) : -1;
    if (roomOutlines && wallMassSystem && edgeIndex !== undefined && edgeIndex > -1) {
      resolvedLateralOffset = -entityLateralOffsetFromWallDatum({
        room: ensureDefined(window.frontRoom),
        edgeIndex,
        roomOutlines,
        wallMassSystem,
        placement: {
          lateralDatum: shutterSettings.lateralDatum ?? WallLateralDatumAnchorAll.ROOM_EDGE,
          lateralOffsetNormalized: false,
          lateralOffset: -resolvedLateralOffset,
        },
        resolvedParam: 1,
      });
    }
    if (resolvedLateralOffset !== 0) {
      glassPlane = planeMoveAlongNormal(glassPlane, resolvedLateralOffset);
    }
  }

  const leftPane =
    panelType === WindowPanelType.FAUX_SHUTTER
      ? addFauxShutterPane(shuttersPlacement, glassPlane, exteriorOutline, true, minZ, shutterArchRadius)
      : addShutterPane(shuttersPlacement, glassPlane, exteriorOutline, true, minZ, shutterArchRadius);
  const rightPane =
    panelType === WindowPanelType.FAUX_SHUTTER
      ? addFauxShutterPane(shuttersPlacement, glassPlane, exteriorOutline, false, minZ, shutterArchRadius)
      : addShutterPane(shuttersPlacement, glassPlane, exteriorOutline, false, minZ, shutterArchRadius);

  if (!leftPane || !rightPane) {
    issues.push({
      type: BuildingGenerationIssueType.INVALID_WINDOW_SHUTTER_GEOMETRY,
      handle,
    });

    return null;
  }

  windowShutters.decorations.addMany(
    [...leftPane, ...rightPane],
    exteriorMaterialOverrideNew(shutterMaterial, uvGenerationForWindowShutter(), shutterViewEntityDescriptor),
  );

  return windowShutters;
}

function addFauxShutterPane(
  shutterPlacement: WindowShuttersPlacement,
  shutterPlane: Plane,
  opening: PolyLine2,
  leftPane: boolean,
  minZ: number = Number.NEGATIVE_INFINITY,
  archRadius?: number,
): Geom[] | undefined {
  const { size, settings, bottom, height } = shutterPlacement;

  const windowAABB = polyLine2AABB2(opening);
  const scaleFactorX = size / (windowAABB.maxX - windowAABB.minX);
  const scaleFactorY = height / (windowAABB.maxY - windowAABB.minY);
  const adjustedOpening = polyLine2MoveCopy(opening, 0, bottom - windowAABB.minY);
  let shutterLine = polyLine2Reverse(
    polyLine2NonUniformScaleAndKeepOrigin(adjustedOpening, scaleFactorX, scaleFactorY),
  );
  const shutterAABB = polyLine2AABB2(shutterLine);

  const winWidth = windowAABB.maxX - windowAABB.minX;
  const shutterWidth = winWidth / 2;

  const shutterSpacing = 0.25;

  if (leftPane) {
    rect2Extend(shutterLine, 2, shutterSpacing / 2);
    polyLine2Move(shutterLine, winWidth / 2, 0);
  } else {
    rect2Extend(shutterLine, 0, shutterSpacing / 2);
    polyLine2Move(shutterLine, -winWidth / 2, 0);
  }

  // Make sure the shutters stay above the wainscoting.
  if (shutterAABB.minY + shutterPlane.originZ < minZ) {
    for (let i = 1, ic = shutterLine.length; i < ic; i += 2) {
      shutterLine[i] = Math.max(shutterLine[i], minZ);
    }
  }

  const newStart = leftPane ? -shutterWidth : windowAABB.maxX - windowAABB.minX;

  // Nudge the actual start a little to fix z fighting at certain angles.
  shutterLine = polyLine2Move(shutterLine, newStart * 0.999, 0);

  if (settings) {
    switch (settings.shutterType) {
      case ShutterType.PANEL:
        return windowShutterPanel(shutterLine, shutterPlane, !leftPane, archRadius);
      case ShutterType.VENTED:
        return windowShutterVent(shutterLine, shutterPlane, !leftPane, archRadius);
      case ShutterType.DISABLED:
      // allows faux shutters to be generated when shutter settings are disabled
      case ShutterType.SIMPLE_SLATS:
        return windowShutterSlats(
          shutterLine,
          shutterPlane,
          settings as SimpleSlatShutterSettings,
          !leftPane,
          archRadius,
        );
      case ShutterType.Z_SLATS:
        return shutterZSlats(shutterLine, shutterPlane, !leftPane, archRadius);
      case ShutterType.GENERIC:
        return windowShutterGeneric(shutterLine, shutterPlane, !leftPane, archRadius);
    }
  }
  return undefined;
}

function addShutterPane(
  shutterPlacement: WindowShuttersPlacement,
  shutterPlane: Plane,
  opening: PolyLine2,
  leftPane: boolean,
  minZ: number = Number.NEGATIVE_INFINITY,
  archRadius?: number,
): Geom[] | undefined {
  const { size, settings, bottom, height, addLeft, addRight } = shutterPlacement;

  if (
    !settings ||
    settings.shutterType === ShutterType.DISABLED ||
    (leftPane && !addLeft) ||
    (!leftPane && !addRight)
  ) {
    return [];
  }

  const { horizontalOffset = 0 } = settings;

  const windowAABB = polyLine2AABB2(opening);
  const scaleFactorX = size / (windowAABB.maxX - windowAABB.minX);
  const scaleFactorY = height / (windowAABB.maxY - windowAABB.minY);
  const adjustedOpening = polyLine2MoveCopy(opening, 0, bottom - windowAABB.minY);
  let shutterLine = polyLine2Reverse(
    polyLine2NonUniformScaleAndKeepOrigin(adjustedOpening, scaleFactorX, scaleFactorY),
  );
  const shutterAABB = polyLine2AABB2(shutterLine);
  const shutterWidth = shutterAABB.maxX - shutterAABB.minX;

  // adjust archRadius if smaller than shutter width
  if (archRadius !== undefined && archRadius < shutterWidth) {
    return [];
  }

  // Make sure the shutters stay above the wainscoting.
  if (shutterAABB.minY + shutterPlane.originZ < minZ) {
    for (let i = 1, ic = shutterLine.length; i < ic; i += 2) {
      shutterLine[i] = Math.max(shutterLine[i], minZ);
    }
  }

  const newStart = leftPane ? -shutterWidth - horizontalOffset : windowAABB.maxX - windowAABB.minX + horizontalOffset;

  // Nudge the actual start a little to fix z fighting at certain angles.
  shutterLine = polyLine2Move(shutterLine, newStart * 0.999, 0);

  switch (settings.shutterType) {
    case ShutterType.PANEL:
      return windowShutterPanel(shutterLine, shutterPlane, leftPane, archRadius);
    case ShutterType.VENTED:
      return windowShutterVent(shutterLine, shutterPlane, leftPane, archRadius);
    case ShutterType.SIMPLE_SLATS:
      return windowShutterSlats(shutterLine, shutterPlane, settings as SimpleSlatShutterSettings, leftPane, archRadius);
    case ShutterType.Z_SLATS:
      return shutterZSlats(shutterLine, shutterPlane, leftPane, archRadius);
    case ShutterType.GENERIC:
      return windowShutterGeneric(shutterLine, shutterPlane, leftPane, archRadius);
  }
}

function windowShutterGeneric(
  outline: PolyLine2,
  shutterPlane: Plane,
  leftPane: boolean,
  archRadius?: number,
): Extrusion[] {
  const ext: Extrusion[] = [];

  if (archRadius) {
    const { shutterArchedOutline, arcHeight } = archedShutterGeom(outline, archRadius, leftPane);
    const shutterAABB = polyLine2AABB2(outline);

    // case where the arc extends past bottom edge of shutter (arc is taller than the shutter itself)
    if (arcHeight > aabb2Height(shutterAABB)) {
      return [];
    }

    const shutterFace = faceNew([shutterArchedOutline], shutterPlane);
    ext.push(extrudeFaceAlongNormal(shutterFace, 3));
  } else {
    const shutterFace = faceNew([outline], shutterPlane);
    ext.push(extrudeFaceAlongNormal(shutterFace, 3));
  }

  return ext;
}

function windowShutterPanel(outline: PolyLine2, shutterPlane: Plane, leftPane: boolean, archRadius?: number): Geom[] {
  const SIDE_TRIM_WIDTH = 2;
  const MID_TRIM_WIDTH = 3;
  const DEPTH = 1;
  const TOP_WIDTH = 3;
  const LOW_WIDTH = 4;
  const PROFILE_DEPTH = 0.5;
  const shutterAABB = polyLine2AABB2(outline);
  const shutterWidth = shutterAABB.maxX - shutterAABB.minX;
  const shutterHeight = shutterAABB.maxY - shutterAABB.minY;
  const PANE_OFFSET = shutterWidth < 14 ? 1.5 : 2.5;

  // case where shutter width is too small to create inner pane detailing
  if (SIDE_TRIM_WIDTH * 2 + PANE_OFFSET * 2 + DIST_EPSILON > shutterWidth) {
    return [];
  }

  const moveAmt = 2;
  const shutterNormal = planeNormal(shutterPlane);
  const plane = planeCopyMoved(shutterPlane, shutterNormal[0] * moveAmt, shutterNormal[1] * moveAmt, 0);
  const panePlane = planeCopyMoved(plane, shutterNormal[0] * 0.5, shutterNormal[1] * 0.5, 0);
  const [nx, ny, nz] = planeNormal(panePlane);

  const panelHeight = (shutterHeight - TOP_WIDTH - LOW_WIDTH - MID_TRIM_WIDTH) / 2;

  const extrusions: Geom[] = [];

  const makeRectPane = (panePl: PolyLine2) => {
    const inPanePl = offsetPolyLine2(panePl, PANE_OFFSET);
    polyLine2SetCW(inPanePl);
    const smallPane = faceNew([inPanePl], plane);
    extrusions.push(extrudeFaceAlongNormal(smallPane, DEPTH));
    const rail = polyLine2OnPlane(panePlane, panePl);
    extrusions.push(extrudeFaceAlongPolyLine3([polyLine2New([0, 0, PANE_OFFSET, PROFILE_DEPTH])], rail, nx, ny, nz));
  };

  // make low trim
  const lowPl = polyLine2ThickenedEdge(outline, 3, LOW_WIDTH);
  rect2Extend(lowPl, 1, -SIDE_TRIM_WIDTH);
  rect2Extend(lowPl, 3, -SIDE_TRIM_WIDTH);
  polyLine2SetCW(lowPl);
  const lowTrim = faceNew([lowPl], plane);
  extrusions.push(extrudeFaceAlongNormal(lowTrim, DEPTH));

  if (archRadius) {
    const { arcPl, rightEdge, leftEdge, arcHeight } = archedShutterGeom(outline, archRadius, leftPane);

    // check if bottom of arc polyline is too low to build the inner panel
    if (arcHeight > shutterHeight - (PANE_OFFSET + LOW_WIDTH)) {
      return [];
    }

    const archedPanelPl = offsetInnerArchedPanel(leftEdge, arcPl, rightEdge, SIDE_TRIM_WIDTH + PANE_OFFSET);
    const archedPanelPlLowDatum =
      Math.min(archedPanelPl[3], archedPanelPl[archedPanelPl.length - 3]) - shutterAABB.minY;

    // check if bottom of the offset panel arc polyline is too low to build inner panel
    if (archedPanelPlLowDatum < PANE_OFFSET + LOW_WIDTH) {
      return [];
    }

    const shutterTrimOuterPl = makeArchedOutlineFromEdges([leftEdge, arcPl, rightEdge]);
    const shutterTrimInnerPl = offsetInnerArchedPanel(leftEdge, arcPl, rightEdge, SIDE_TRIM_WIDTH);

    // close trim inner polyline
    const bottomLeftEdge = polyLine2New([
      shutterTrimInnerPl[0],
      shutterTrimInnerPl[1],
      shutterTrimOuterPl[0],
      shutterTrimOuterPl[1],
    ]);
    const shutterInnerPlReversed = polyLine2ReverseCopy(shutterTrimInnerPl);
    const bottomRightEdge = polyLine2New([
      shutterTrimOuterPl[shutterTrimOuterPl.length - 2],
      shutterTrimOuterPl[shutterTrimOuterPl.length - 1],
      shutterInnerPlReversed[0],
      shutterInnerPlReversed[1],
    ]);

    const shutterTrimPl = makeArchedOutlineFromEdges(
      [shutterTrimOuterPl, bottomRightEdge, shutterInnerPlReversed, bottomLeftEdge],
      true,
    );

    const outerFrame = faceNew([shutterTrimPl], plane);
    extrusions.push(extrudeFaceAlongNormal(outerFrame, DEPTH - 0.125));

    // make upper arched pane
    const lowerPanelLowDatum = LOW_WIDTH + PANE_OFFSET;
    const upperPanelLowDatum = lowerPanelLowDatum + panelHeight + MID_TRIM_WIDTH;
    const isDoublePaneShutter = upperPanelLowDatum <= archedPanelPlLowDatum;
    const panelLowDatum = isDoublePaneShutter ? upperPanelLowDatum : lowerPanelLowDatum;
    polyLine2ExtendEnd(archedPanelPl, -panelLowDatum);
    polyLine2ExtendStart(archedPanelPl, -panelLowDatum);
    const archedPanelPlClosed = polyLine2Close(archedPanelPl);

    const upperPanel = faceNew([archedPanelPlClosed], plane);
    extrusions.push(extrudeFaceAlongNormal(upperPanel, DEPTH));

    // make inset
    const upperPaneRail = polyLine2OnPlane(panePlane, polyLine2ReverseCopy(archedPanelPlClosed));
    extrusions.push(
      extrudeFaceAlongPolyLine3([polyLine2New([0, PROFILE_DEPTH, PANE_OFFSET, 0])], upperPaneRail, nx, ny, nz),
    );

    if (!isDoublePaneShutter) {
      return extrusions;
    }
  } else {
    // make rectangular trim and upper panel
    const leftPl = polyLine2ThickenedEdge(outline, 2, SIDE_TRIM_WIDTH);
    const rightPl = polyLine2ThickenedEdge(outline, 0, SIDE_TRIM_WIDTH);
    const topPl = polyLine2ThickenedEdge(outline, 1, TOP_WIDTH);

    // BRING IN THE TOP AND BOTTOM TO CLEAR THE SIDE TRIM
    rect2Extend(topPl, 1, -SIDE_TRIM_WIDTH);
    rect2Extend(topPl, 3, -SIDE_TRIM_WIDTH);

    polyLine2SetCW(topPl);

    polyLine2SetCW(leftPl);
    polyLine2SetCW(rightPl);

    const leftTrim = faceNew([leftPl], plane);
    const rightTrim = faceNew([rightPl], plane);
    const topTrim = faceNew([topPl], plane);

    extrusions.push(extrudeFaceAlongNormal(leftTrim, DEPTH - 0.125));
    extrusions.push(extrudeFaceAlongNormal(rightTrim, DEPTH - 0.125));
    extrusions.push(extrudeFaceAlongNormal(topTrim, DEPTH));

    // make top pane
    const topPanePl = polyLine2ThickenedEdge(topPl, 1, -panelHeight);
    makeRectPane(topPanePl);
  }

  // make lower pane and middle trim
  const lowPanePl = polyLine2ThickenedEdge(lowPl, 1, -panelHeight);
  makeRectPane(lowPanePl);

  const midTrimPl = polyLine2ThickenedEdge(lowPanePl, 2, -MID_TRIM_WIDTH);
  polyLine2SetCW(midTrimPl);
  const midTrim = faceNew([midTrimPl], plane);
  extrusions.push(extrudeFaceAlongNormal(midTrim, DEPTH));

  return extrusions;
}

function windowShutterVent(
  outline: PolyLine2,
  shutterPlane: Plane,
  leftPane: boolean,
  archRadius?: number,
): Extrusion[] | undefined {
  const ventAABB = polyLine2AABB2(outline);
  const SLAT_DEPTH = 2;
  const FRAME_WIDTH = 1.5;
  const FRAME_DEPTH = 3;
  const moveAmt = 2;
  const shutterNormal = planeNormal(shutterPlane);
  const slatPlane = planeCopyMoved(shutterPlane, shutterNormal[0] * moveAmt, shutterNormal[1] * moveAmt, 0);

  const numSlats = Math.floor((ventAABB.maxY - ventAABB.minY) / 2);

  const extrusions: Extrusion[] = [];

  if (archRadius) {
    const {
      shutterArchedOutline: shutterOuterPl,
      leftEdge,
      arcPl,
      rightEdge,
    } = archedShutterGeom(outline, archRadius, leftPane);

    const shutterInnerPl = offsetInnerArchedPanel(leftEdge, arcPl, rightEdge, FRAME_WIDTH, true);

    // check if bottom edge can be raised without intersecting arc
    const shutterInnerArcPlLowDatum =
      Math.min(shutterInnerPl[3], shutterInnerPl[shutterInnerPl.length - 5]) - ventAABB.minY;

    if (shutterInnerArcPlLowDatum < FRAME_WIDTH) {
      return [];
    }

    extrusions.push(
      ...extrusions,
      ...ventExtrusion({
        outline: shutterInnerPl,
        plane: slatPlane,
        divNum: numSlats,
        thickness: SLAT_DEPTH,
        clipAgainstOutline: true,
      }),
    );

    // raise bottom of inner outline
    shutterInnerPl[shutterInnerPl.length - 1] += FRAME_WIDTH;
    shutterInnerPl[shutterInnerPl.length - 3] += FRAME_WIDTH;

    const ventFrame = faceNew([shutterOuterPl, polyLine2Reverse(shutterInnerPl)], shutterPlane);
    const frame = extrudeFaceAlongNormal(ventFrame, FRAME_DEPTH);
    extrusions.push(frame);
  } else {
    const scaleX = 1.0 - (FRAME_WIDTH * 2.0) / (ventAABB.maxX - ventAABB.minX);
    const ventsOutline = polyLine2ScaleXAndKeepOrigin(polyLine2MoveCopy(outline, FRAME_WIDTH, 0), scaleX);

    extrusions.push(
      ...extrusions,
      ...ventExtrusion({
        outline: ventsOutline,
        plane: slatPlane,
        divNum: numSlats,
        thickness: SLAT_DEPTH,
      }),
    );

    const ventFrame = faceNew([outline, polyLine2Reverse(offsetPolyLine2(outline, FRAME_WIDTH))], shutterPlane);
    const frame = extrudeFaceAlongNormal(ventFrame, FRAME_DEPTH);
    extrusions.push(frame);
  }
  return extrusions;
}

function windowShutterSlats(
  outline: PolyLine2,
  shutterPlane: Plane,
  settings: SimpleSlatShutterSettings,
  leftPane: boolean,
  archRadius?: number,
): Extrusion[] {
  const SLAT_DEPTH = 0.5;
  const SLAT_WIDTH = 3.5;

  return makeSimpleSlats(outline, shutterPlane, leftPane, SLAT_DEPTH, SLAT_WIDTH, archRadius, settings?.midSlat);
}

function shutterZSlats(outline: PolyLine2, shutterPlane: Plane, leftPane: boolean, archRadius?: number): Extrusion[] {
  const SLAT_WIDTH = 3.5;
  const SLAT_DEPTH = 0.75;

  return makeSimpleSlats(outline, shutterPlane, leftPane, SLAT_DEPTH, SLAT_WIDTH, archRadius, false, true);
}

function makeSimpleSlats(
  outline: PolyLine2,
  shutterPlane: Plane,
  leftPane: boolean,
  slatDepth: number,
  slatWidth: number,
  archRadius?: number,
  addMiddleSlat: boolean = false,
  addZSlat: boolean = false,
): Extrusion[] {
  const shutterAABB = polyLine2AABB2(outline);
  const shutterTotalWidth = shutterAABB.maxX - shutterAABB.minX;
  const shutterTotalHeight = shutterAABB.maxY - shutterAABB.minY;
  const shutterNormal = planeNormal(shutterPlane);
  const moveAmt = WINDOW_EXTERIOR_FRAME_DEPTH + slatDepth + 2;
  const slatPlane = planeCopyMoved(shutterPlane, moveAmt * shutterNormal[0], moveAmt * shutterNormal[1], 0);

  let numSlats = Math.floor(shutterTotalWidth / 3.5) + 1;

  // make the number of slats odd
  if (numSlats % 2 === 0) {
    numSlats += 1;
  }

  // slice up that outline
  const divInput: number[] = [];
  for (let i = 0; i < numSlats; i += 1) {
    if (i % 2 === 0) {
      divInput.push(slatWidth);
    } else {
      divInput.push(0);
    }
  }

  const extrusions: Extrusion[] = [];
  let archedHeightAdjust = 0;

  if (archRadius) {
    const { shutterArchedOutline, arcPl, bottomEdge, arcHeight } = archedShutterGeom(outline, archRadius, leftPane);
    archedHeightAdjust = arcHeight;
    const numSpaces = Math.floor(numSlats / 2);
    const edgeLength = polyLine2EdgeLength(bottomEdge, 0);
    const slatSpacingDistance = (edgeLength - (numSlats - numSpaces) * slatWidth) / numSpaces;
    let arcRemainder = polyLine2Copy(arcPl);
    let originX = bottomEdge[2];

    const makeSlat = (slatOutline: PolyLine2) => {
      const slat = faceNew([slatOutline], slatPlane);
      extrusions.push(extrudeFaceAlongNormal(slat, WINDOW_EXTERIOR_FRAME_DEPTH * slatDepth));
    };

    // iteratively split the arc
    for (let i = 0; i < divInput.length - 1; i++) {
      const div = divInput[i];
      originX += div || slatSpacingDistance;
      const segments = splitArcPlRay(arcRemainder, originX, bottomEdge[3], 0, 1);
      if (segments === null) {
        continue;
      }
      const [leftArcPlSegment, rightArcPlSegment] = segments;
      // skip adding spacing segments
      if (div !== 0) {
        const slatOutline = polyLine2ConcatAll(
          [leftArcPlSegment, [originX, bottomEdge[3]], [originX - div, bottomEdge[3]]],
          true,
        );
        makeSlat(slatOutline);
      }
      arcRemainder = rightArcPlSegment;
    }

    // make rightmost slat
    const rightMostSlatOutline = polyLine2ConcatAll(
      [arcRemainder, [originX + slatWidth, bottomEdge[3]], [originX, bottomEdge[3]]],
      true,
    );
    makeSlat(rightMostSlatOutline);

    const background = faceNew(
      [shutterArchedOutline],
      planeCopyMoved(slatPlane, -0.5 * shutterNormal[0], -0.5 * shutterNormal[1], 0),
    );

    extrusions.push(extrudeFaceAlongNormal(background, 0.1));
  } else {
    const verticalSlatDivs = rect2MultiSplitFlex(outline, divInput, false);
    for (let i = 0; i < verticalSlatDivs.length; i += 1) {
      if (i % 2 === 0) {
        const slat = faceNew([verticalSlatDivs[i]], slatPlane);
        extrusions.push(extrudeFaceAlongNormal(slat, WINDOW_EXTERIOR_FRAME_DEPTH * slatDepth));
      }
    }

    const background = faceNew(
      [outline],
      planeCopyMoved(slatPlane, -0.5 * shutterNormal[0], -0.5 * shutterNormal[1], 0),
    );

    extrusions.push(extrudeFaceAlongNormal(background, 0.1));
  }

  const horizontalSlatWidth = 3.5;

  const horizontalSlats = rect2MultiSplitFlex(
    outline,
    [
      shutterTotalHeight / 8,
      horizontalSlatWidth,
      0,
      horizontalSlatWidth,
      0,
      horizontalSlatWidth,
      Math.max(shutterTotalHeight / 8, archedHeightAdjust),
    ],
    true,
  );
  const horizontalSlatPlane = planeCopyMoved(slatPlane, shutterNormal[0] * slatDepth, shutterNormal[1] * slatDepth, 0);

  if (addMiddleSlat) {
    const midSlat = faceNew([horizontalSlats[3]], horizontalSlatPlane);
    extrusions.push(extrudeFaceAlongNormal(midSlat, WINDOW_EXTERIOR_FRAME_DEPTH * slatDepth));
  }

  if (addZSlat) {
    const zPl = polyLine2ThickenedEdge(outline, 2, shutterTotalWidth);
    rect2Extend(zPl, 1, -(horizontalSlatWidth + Math.max(shutterTotalHeight / 8, archedHeightAdjust)));
    rect2Extend(zPl, 3, -(horizontalSlatWidth + shutterTotalHeight / 8));
    const zShape = polyLine2New([
      zPl[2] - 3.5,
      zPl[3],
      zPl[2],
      zPl[3],
      zPl[6] + 3.5,
      zPl[7],
      zPl[6],
      zPl[7],
      zPl[2] - 3.5,
      zPl[3],
    ]);
    if (leftPane) {
      const [lX, lY] = line2MidPt(zPl[0], zPl[1], zPl[2], zPl[3]);
      // const [tX, tY] = line2MidPt(zPl[6], zPl[7], zPl[8], zPl[9]);
      const axis = ray2New(lX, lY, 0, 1);
      polyLine2Reflect(zShape, axis);
      polyLine2Move(zShape, -shutterTotalWidth, 0);
    }

    polyLine2SetCW(zShape);

    const test = faceNew([zShape], planeCopyMoved(slatPlane, -0.5 * shutterNormal[0], -0.5 * shutterNormal[1], 0));

    extrusions.push(extrudeFaceAlongNormal(test, 1.25));
  }

  const topSlat = faceNew([horizontalSlats[1]], horizontalSlatPlane);
  const lowSlat = faceNew([horizontalSlats[5]], horizontalSlatPlane);

  extrusions.push(extrudeFaceAlongNormal(topSlat, WINDOW_EXTERIOR_FRAME_DEPTH * slatDepth));
  extrusions.push(extrudeFaceAlongNormal(lowSlat, WINDOW_EXTERIOR_FRAME_DEPTH * slatDepth));

  return extrusions;
}

interface ArchShutterGeom {
  readonly shutterArchedOutline: PolyLine2;
  readonly arcPl: PolyLine2;
  readonly rightEdge: PolyLine2;
  readonly bottomEdge: PolyLine2;
  readonly leftEdge: PolyLine2;
  readonly bottomBox: PolyLine2;
  readonly arcHeight: number;
}

function archedShutterGeom(outline: PolyLine2, archRadius: number, leftPane: boolean): ArchShutterGeom {
  // overall dims
  const shutterAABB = polyLine2AABB2(outline);
  const shutterWidth = shutterAABB.maxX - shutterAABB.minX;
  const shutterHeight = shutterAABB.maxY - shutterAABB.minY;
  // arcPl dims
  const arcRadius = Math.max(archRadius ?? shutterWidth, shutterWidth); //the minimum allowed radius is half the width
  const arcAngleRads = Math.asin(shutterWidth / arcRadius);
  const arcHeight = arcRadius * (1 - Math.cos(arcAngleRads));

  // shutter edges
  const bottomBox = polyLine2Reverse(polyLine2ThickenedEdge(outline, 3, shutterHeight - arcHeight));
  const leftEdge = polyLine2Edge(leftPane ? outline : bottomBox, 0);
  const bottomEdge = polyLine2Edge(leftPane ? outline : bottomBox, 3);
  const rightEdge = polyLine2Edge(leftPane ? bottomBox : outline, 2);

  //make arcPl curve
  const [arcX, arcY] = leftPane ? [rightEdge[0], rightEdge[1]] : [leftEdge[2], leftEdge[3]];
  const [arcCenterX, arcCenterY] = leftPane
    ? [leftEdge[2], leftEdge[3] - arcRadius]
    : [rightEdge[0], rightEdge[1] - arcRadius];

  const arcPl = arc2ByCenterStartAngle(
    arcCenterX,
    arcCenterY,
    arcX,
    arcY,
    leftPane ? arcAngleRads : -arcAngleRads,
    0.01,
    5,
    8,
  );

  if (leftPane) {
    polyLine2Reverse(arcPl);
  }

  //make shutter outline
  const shutterArchedOutline = polyLine2Close(
    polyLine2Simplify(polyLine2ConcatAll([rightEdge, bottomEdge, leftEdge, arcPl])),
  );

  return { shutterArchedOutline, arcPl, rightEdge, bottomEdge, leftEdge, bottomBox, arcHeight };
}

function makeArchedOutlineFromEdges(edges: PolyLine2[], close: boolean = false): PolyLine2 {
  const outline = polyLine2Simplify(polyLine2ConcatAll(edges));
  if (close) {
    polyLine2Close(outline);
  }
  return outline;
}
